import { slotAnzahl, spalten } from "./form.ts";
import type { Katalog, Listenbereich, Quellendaten, SlotFelder, UiDaten, Werte } from "./modell.ts";
import { ersetze, ersetzeZeilen } from "./platzhalter.ts";
import { mitZustand } from "./zustand.ts";

export type Daten = Record<string, Quellendaten>;

export type SlotAnsicht = {
  index: number;
  art: "fest" | "eintrag" | "blaettern" | "leer" | "rahmen";
  symbol: string;
  anzahl: number;
  name: string;
  beschreibung: string[];
  leuchten: boolean;
  aktion?: string;
  wert?: string;
  bestaetigung?: string;
  bedrockBild?: string;
};

export type JavaAnsicht = { titel: string; spalten: number; seite: number; seiten: number; slots: SlotAnsicht[] };

export type Verweise = (id: string) => string | undefined;

export const STANDARD_BLAETTERN: Record<"zurueck" | "weiter" | "info", SlotFelder> = {
  zurueck: { symbol: "minecraft:arrow", name: "« Zurück" },
  weiter: { symbol: "minecraft:arrow", name: "Weiter »" },
  info: { symbol: "minecraft:paper", name: "Seite {seite} / {seiten}" },
};

export type Aufgeloest = Omit<SlotAnsicht, "index" | "art">;

function anzahlAus(roh: number | string | undefined, werte: Werte): number {
  if (roh === undefined) return 1;
  const n = typeof roh === "number" ? roh : Number.parseInt(ersetze(roh, werte), 10);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(64, Math.floor(n));
}

/** Setzt Werte in alle Felder eines Slots ein. */
export function loeseSlot(felder: SlotFelder, werte: Werte, katalog: Katalog, verweise?: Verweise): Aufgeloest {
  const r: Aufgeloest = {
    symbol: ersetze(felder.symbol, werte),
    anzahl: anzahlAus(felder.anzahl, werte),
    name: ersetze(felder.name ?? "", werte),
    beschreibung: ersetzeZeilen(felder.beschreibung ?? [], werte),
    leuchten: felder.leuchten ?? false,
  };
  if (felder.aktion) r.aktion = felder.aktion;
  if (felder.wert !== undefined) {
    let wert = ersetze(felder.wert, werte);
    const aktion = katalog.aktionen.find((a) => a.schluessel === felder.aktion);
    if (aktion?.argument === "ui" && verweise) wert = verweise(wert) ?? wert;
    r.wert = wert;
  }
  if (felder.bestaetigung) r.bestaetigung = ersetze(felder.bestaetigung, werte);
  if (felder.bedrockBild) r.bedrockBild = felder.bedrockBild;
  return r;
}

export function eintraegeVon(l: Listenbereich, daten: Daten): Quellendaten["eintraege"] {
  return daten[l.quelle]?.eintraege ?? [];
}

export function seitenzahl(ui: UiDaten, daten: Daten): number {
  let seiten = 1;
  for (const l of ui.listen) {
    if (l.slots.length === 0) continue;
    seiten = Math.max(seiten, Math.ceil(eintraegeVon(l, daten).length / l.slots.length));
  }
  return seiten;
}

export function menueWerte(ui: UiDaten, daten: Daten, seite: number, seiten: number): Werte {
  const quelle = ui.quelle ? daten[ui.quelle]?.werte : undefined;
  return { ...(quelle ?? {}), seite: String(seite + 1), seiten: String(seiten) };
}

export function menueZustand(ui: UiDaten, daten: Daten): string | undefined {
  return ui.quelle ? daten[ui.quelle]?.zustand : undefined;
}

/** Fester Slot nach Menü-Zustand; null, wenn er in diesem Zustand Rahmen ist. */
export function festerSlot(ui: UiDaten, index: number, daten: Daten): SlotFelder | null {
  const slot = ui.slots[index];
  if (!slot) return null;
  const { zustaende, ...felder } = slot;
  const r = mitZustand<SlotFelder>(felder, zustaende, menueZustand(ui, daten));
  return r === "leer" ? null : r;
}

export function eintragFelder(l: Listenbereich, zustand: string | undefined): SlotFelder {
  const r = mitZustand<SlotFelder>(l.vorlage, l.zustaende, zustand);
  return r === "leer" ? l.vorlage : r;
}

export function titelMitVorlage(ui: UiDaten, katalog: Katalog, werte: Werte): string {
  return ersetze(katalog.darstellung.titelVorlage.split("{titel}").join(ui.titel), werte);
}

export function javaAnsicht(ui: UiDaten, katalog: Katalog, daten: Daten, seite: number, verweise?: Verweise): JavaAnsicht {
  const n = slotAnzahl(ui.form);
  const seiten = seitenzahl(ui, daten);
  const s = Math.min(Math.max(0, Math.floor(seite)), seiten - 1);
  const werte = menueWerte(ui, daten, s, seiten);
  const rahmen = (index: number): SlotAnsicht => ({ index, art: "rahmen", symbol: ui.rahmen.symbol, anzahl: 1, name: "", beschreibung: [], leuchten: false });
  const slots: SlotAnsicht[] = Array.from({ length: n }, (_, i) => rahmen(i));
  const setze = (index: number, art: SlotAnsicht["art"], a: Aufgeloest) => {
    if (index < n) slots[index] = { index, art, ...a };
  };

  for (const k of Object.keys(ui.slots)) {
    const i = Number(k);
    const felder = festerSlot(ui, i, daten);
    if (felder) setze(i, "fest", loeseSlot(felder, werte, katalog, verweise));
  }

  for (const l of ui.listen) {
    const plaetze = [...l.slots].sort((a, b) => a - b);
    const alle = eintraegeVon(l, daten);
    if (alle.length === 0 && l.leer && plaetze.length > 0) {
      setze(plaetze[Math.floor(plaetze.length / 2)]!, "leer", loeseSlot(l.leer, werte, katalog, verweise));
    }
    alle.slice(s * plaetze.length, (s + 1) * plaetze.length).forEach((e, i) => {
      setze(plaetze[i]!, "eintrag", loeseSlot(eintragFelder(l, e.zustand), { ...werte, ...e.werte }, katalog, verweise));
    });
    const b = l.blaettern;
    if (!b) continue;
    const zeige = (art: "zurueck" | "weiter" | "info", aktiv: boolean) => {
      const eigen = b[art];
      if (!eigen) return;
      const { slot, ...felder } = eigen;
      if (aktiv) setze(slot, "blaettern", loeseSlot({ ...STANDARD_BLAETTERN[art], ...felder }, werte, katalog, verweise));
    };
    zeige("zurueck", s > 0);
    zeige("weiter", s < seiten - 1);
    zeige("info", true);
  }

  return { titel: titelMitVorlage(ui, katalog, werte), spalten: spalten(ui.form), seite: s, seiten, slots };
}

export function beispielDaten(ui: UiDaten, katalog: Katalog): Daten {
  const daten: Daten = {};
  for (const q of [ui.quelle, ...ui.listen.map((l) => l.quelle)]) {
    if (!q || daten[q]) continue;
    const quelle = katalog.quellen.find((x) => x.schluessel === q);
    if (quelle) daten[q] = quelle.beispiel;
  }
  return daten;
}
